import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapActions, mapGetters, mapState } from 'vuex'
import { emitter, Spinner } from '@kwixeo/interface-vue'
import moment from 'moment'
import { KFormSelect, KFormSpinner } from '@kwixeo/form-vue'
import { BFormGroup, BFormRadioGroup, BFormRadio, BBadge, VBTooltip, BModal, BCard, BAlert } from 'bootstrap-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Command from '../Command/Command.vue'
import CommandMixin from '../../mixins/CommandMixin'

@Component({
  components: {
    BAlert,
    KFormSelect, KFormSpinner,
    BFormGroup, BFormRadioGroup, BFormRadio, BBadge, BModal, BCard,
    FontAwesomeIcon,
    Spinner,
    Command
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  computed: {
    ...mapState('app', ['app']),
    ...mapGetters('user', ['team']),
    ...mapState('globalDatas', ['teamUsers']),
    ...mapState('dynamicModals', ['instances']),
    ...mapState('subscriptions', ['appModules'])
  },
  methods: {
    ...mapActions('globalDatas', ['loadTeamUsers']),
    ...mapActions('dynamicModals', ['closeModal']),
    ...mapActions('subscriptions', ['loadAppModules'])
  }
})
export default class Cart extends Mixins(CommandMixin) {
  app!: KwixeoFramework.App
  team!: KwixeoFramework.Team
  loadTeamUsers!: (force?: boolean) => void
  teamUsers!: KwixeoFramework.TeamUsers
  instances!: KwixeoInterface.DynamicModals
  closeModal!: (index: number) => void
  loadAppModules!: (force?: boolean) => Promise<void>
  appModules!: KwixeoSubscriptions.Modules

  @Prop({ required: true }) value!: Partial<KwixeoSubscriptions.CartContent>
  @Prop({ default: () => ([]) }) tryModules!: KwixeoSubscriptions.Modules

  private duration = 12
  private loading = false
  private toPaid = false
  private addUsers = false
  private userQuantity = 0
  private addModules = false
  private command: KwixeoSubscriptions.Command|null = null

  private cart: KwixeoSubscriptions.CartContent = {
    modules: [],
    users: 0
  }

  async mounted (): Promise<void> {
    this.loading = true
    await this.loadTeamUsers()
    await this.loadAppModules()
    this.loading = false

    if (this.subscription.duration) {
      this.duration = this.subscription.duration
    }

    const gestionco = (this.team.app_modules ?? []).find(module => module.key === 'sales-gestionco')
    if (gestionco && gestionco.is_trying) {
      if (gestionco.is_expired) {
        this.addTryModules()
      } else {
        if (this.cart.modules.findIndex(module => module.key === 'sales-gestionco') === -1) {
          this.cart.modules.push(gestionco)
        }
      }
    }
  }

  get cartModules (): KwixeoSubscriptions.Modules {
    return this.cart.modules.sort((a, b) => {
      if (a.key === 'sales-gestionco') return -2
      else if (b.key === 'sales-gestionco') return 2
      else if (a.name < b.name) return -1
      else if (a.name > b.name) return 1
      else return 0
    })
  }

  get availableModules (): KwixeoSubscriptions.Modules {
    return this.appModules.filter(module => {
      return module.is_paying && this.cart.modules.find(val => val.key === module.key) === undefined
        && (!module.team?.status || module.team.try)
    })
  }

  get isUpdate (): boolean {
    return this.subscription.period_begin !== null && this.subscription.expire_at !== null && moment(this.subscription.expire_at).isAfter()
  }

  get isNewSubscription (): boolean {
    const gestionco = this.tryModule
    return gestionco === undefined || gestionco.is_trying
  }

  get tryModule (): KwixeoSubscriptions.Module|undefined {
    return (this.team.app_modules ?? []).find(module => module.key === 'sales-gestionco')
  }

  get subscriptionIsExpired (): boolean {
    const gestionco = this.tryModule
    return gestionco !== undefined && !gestionco.is_trying && gestionco.is_expired
  }

  get quantity (): number {
    if (this.isUpdate) {
      const full = moment(this.subscription.period_end).diff(this.subscription.period_begin, 'days')
      const partial = moment(this.subscription.period_end).diff(moment(), 'days')
      return this.roundNumber(partial / full, 2)
    }
    return 1
  }

  get today (): string {
    return moment().format('YYYY-MM-DD')
  }

  get subscription (): KwixeoSubscriptions.TeamSubscription {
    return this.team.subscription as KwixeoSubscriptions.TeamSubscription
  }

  get canAddTryModule (): boolean {
    return this.tryModules.filter(module => {
      return this.cart.modules.findIndex(val => val.key === module.key) < 0
    }).length > 0
  }

  get requiredUserNumber (): number {
    let nb = this.teamUsers.length
    if (!this.subscriptionIsExpired && !this.isNewSubscription) {
      nb -= (this.subscription.allowed_user ?? 0)
    }
    return nb < 0 ? 0 : nb
  }

  get unitUserPrice (): (duration?: number|null) => number {
    return (duration: number|null = null) => {
      if (!duration) {
        duration = this.duration
      }
      const unitPrice = this.app.user_month_price
      if (duration === 1 || unitPrice === 0) {
        return unitPrice
      }
      return this.roundNumber(unitPrice * this.annualMonthQuantity, 2)
    }
  }

  get totalUserPrice (): (duration?: number|null) => number {
    return (duration: number|null = null) => {
      return this.roundNumber(this.number(this.unitUserPrice(duration)) * this.number(this.userQuantity) * this.number(this.quantity), 2)
    }
  }

  get modulePrice (): (module: KwixeoSubscriptions.Module, duration?: number|null) => number {
    return (module: KwixeoSubscriptions.Module, duration: number|null = null) => {
      if (!duration) {
        duration = this.duration
      }
      const unitPrice = module.month_price ?? 0
      if (duration === 1 || unitPrice === 0) {
        return unitPrice
      }
      return this.roundNumber(unitPrice * this.annualMonthQuantity, 2)
    }
  }

  get totalModulePrice (): (module: KwixeoSubscriptions.Module, duration?: number|null) => number {
    return (module: KwixeoSubscriptions.Module, duration: number|null = null) => {
      return this.roundNumber(this.modulePrice(module, duration) * this.quantity, 2)
    }
  }

  get annualMonthQuantity (): number {
    return 12 - this.app.year_free_month
  }

  // Get total of content without discount
  get contentTotalHt (): (duration?: number|null) => number {
    return (duration: number|null = null) => {
      if (!duration) {
        duration = this.duration
      }

      let total = 0
      this.cart.modules.forEach(module => {
        total += this.number(this.totalModulePrice(module, duration))
      })
      total += this.number(this.totalUserPrice(duration))

      return this.roundNumber(total, 2)
    }
  }

  // Get discount for annual subscription
  get yearDiscountHt (): number {
    return this.roundNumber(this.contentTotalHt(1) * this.app.year_free_month, 2)
  }

  get firstYearDiscountHt (): number {
    return this.roundNumber(this.yearDiscountHt * this.app.year_free_month_first, 2)
  }

  get totalHt (): (duration?: number|null, applyFirstDiscount?: boolean) => number {
    return (duration: number|null = null, applyFirstDiscount = true) => {
      if (!duration) {
        duration = this.duration
      }

      let total = this.contentTotalHt(duration)

      if (applyFirstDiscount && this.isNewSubscription && duration === 12) {
        total -= this.firstYearDiscountHt
      }

      if (applyFirstDiscount && this.isNewSubscription && duration === 12 && this.team.subscription?.is_sponsored) {
        total -= this.number(this.app.sponsorship_subscriber_amount)
      }

      return this.roundNumber(total, 2)
    }
  }


  get vatRate (): number {
    return this.number(this.subscription.vat_rate ?? this.app.vat_rate)
  }

  get totalTva (): number {
    return this.roundNumber(this.totalHt() * this.vatRate / 100, 2)
  }

  get totalTtc (): number {
    return this.roundNumber(this.totalHt() + this.totalTva, 2)
  }

  get isEmpty (): boolean {
    return this.cart.modules.length === 0 && !this.addUsers
  }

  get monthYearPrice (): number {
    return this.roundNumber(this.totalHt(1) * 12, 2)
  }

  get yearMonthPrice (): number {
    return this.roundNumber(this.totalHt(12, true) / 12, 2)
  }

  get economy (): number {
    return this.roundNumber(this.monthYearPrice - this.totalHt(12), 2)
  }

  @Watch('value', { immediate: true })
  @Watch('teamUsers')
  private watchValue () {
    const userNb = this.requiredUserNumber
    this.cart = Object.assign(
      {
        modules: [],
        users: userNb
      },
      JSON.parse(JSON.stringify(this.value))
    )

    if (this.cart.users > 0) {
      this.addUsers = true
      this.userQuantity = this.cart.users
    }
  }

  private addTryModules (): void {
    this.tryModules.forEach(module => {
      const index = this.cart.modules.findIndex(val => val.key === module.key)
      if (index < 0) {
        this.cart.modules.push(module)
      }
    })
  }

  private addModule (module: KwixeoSubscriptions.Module): void {
    this.cart.modules.push(module)
    if (this.availableModules.length === 0) {
      this.addModules = false
    }
  }

  private removeModule (module: KwixeoSubscriptions.Module, index: number): void {
    Vue.delete(this.cart.modules, index)
    this.$emit('removeModule', module)
  }

  private commandContents (): KwixeoInterface.GenericObject[] {
    const contents: KwixeoInterface.GenericObject[] = []

    this.cart.modules.forEach(module => {
      contents.push({
        element: {
          type: 'module',
          key: module.key
        }
      })
    })

    if (this.addUsers && this.userQuantity > 0) {
      contents.push({
        element: {
          type: 'user'
        },
        quantity: this.userQuantity
      })
    }

    return contents
  }

  private async createCommand (): Promise<void> {
    this.$emit('createCommand')
    this.loading = true
    const data: KwixeoInterface.GenericObject = {
      contents: this.commandContents()
    }
    if (this.isNewSubscription) {
      data.duration = this.duration
    }

    this.$kwSubscriptions.axios.post('/app/command', data)
      .then(response => {
        this.command = response.data
        this.toPaid = true
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(() => {
        this.loading = false
      })
  }

  private backToCart (): void {
    this.toPaid = false
    this.command = null
    this.$emit('backToCart')
  }

  private async goToSubscriptionManagement (): Promise<void> {
    if (this.$route.name !== 'subscription-manage') {
      await this.$router.push({ name: 'subscription-manage'})
    }
    emitter.emit('needSubscribeUser', false)
    const index = this.instances.findIndex(modal => modal.key === 'cart')
    if (index >= 0) {
      this.closeModal(index)
    }
  }
}
